import {
    arrowLeftKeyCode,
    arrowRightKeyCode,
    supportedKeys,
    collectibleSpawnIncreasingRate,
    collectibleSpawnChanceLowerLimit,
    maximumCollectibleSpawnRate,
    collectibleDropMovementIncrement,
    collectibleImageHeight,
    collectibleDeadImageDifference,
    playerImageHeight,
    playerImageWidth,
    collectibleImageWidth,
    deadCollectibleRenderLimit,
    playerMovementPulseStrength,
    maximumPlayerSpeed,
    playerMovementFriction,
    collectibleDeadImageDifferenceX,
} from './constants';

export const getMovementDirectionForKeyPress = keyPressed =>
    keyPressed === arrowLeftKeyCode ? 1 : keyPressed === arrowRightKeyCode ? -1 : 0;

export const getNewPlayerPositionForKeyPress = (playerPosition, playerSpeed) =>
    playerPosition - playerSpeed;

export const updatePlayerSpeedFromKeysPressed = (keysDown, playerSpeed) => {
    let newPlayerSpeed = playerSpeed;
    const multipleKeysPressed = getKeysDownAsArray(keysDown).length > 1;
    if (!multipleKeysPressed && Math.abs(playerSpeed) < maximumPlayerSpeed) {
        const movementPulseDirection = getMovementDirectionForKeyPress(getKeyDown(keysDown));
        newPlayerSpeed = playerSpeed + playerMovementPulseStrength * movementPulseDirection;
    }

    return applyFrictionToPlayerSpeed(newPlayerSpeed);
};

export const updatePlayerSpeedFromAccelerometer = (
    accelerometerValue,
    playerSpeed,
    orientationIsFlipped,
) => {
    const accelerometerLimit = 50;
    const sign = accelerometerValue > 0 ? 1 : -1;
    const limitedAccelerometerValue =
        Math.abs(accelerometerValue) > accelerometerLimit ? accelerometerLimit : accelerometerValue;
    const acclerometerMagnitude = Math.abs(limitedAccelerometerValue) / accelerometerLimit;
    let newPlayerSpeed = -(acclerometerMagnitude * sign * maximumPlayerSpeed);
    return newPlayerSpeed * (orientationIsFlipped ? -1 : 1);
};

export const updatePlayerSpeed = (keysDown, playerSpeed, accelerometerValue) =>
    !accelerometerValue
        ? updatePlayerSpeedFromKeysPressed(keysDown, playerSpeed)
        : updatePlayerSpeedFromAccelerometer(
              accelerometerValue,
              playerSpeed,
              window.screen.orientation.type === 'landscape-secondary',
          );

export const applyFrictionToPlayerSpeed = playerSpeed => {
    const sign = playerSpeed > 0 ? 1 : -1;

    if (Math.abs(playerSpeed) <= playerMovementFriction) {
        return 0;
    } else {
        return playerSpeed - playerMovementFriction * sign;
    }
};

export const limitPlayerPositionToBoundaries = (playerPosition, windowWidth) => {
    const leftBorder = 0;
    const rightBorder = windowWidth - playerImageWidth;
    let newPlayerPosition = playerPosition;
    if (playerPosition < leftBorder) {
        newPlayerPosition = leftBorder;
    }
    if (playerPosition > rightBorder) {
        newPlayerPosition = rightBorder;
    }
    return newPlayerPosition;
};

export const getTimeMillis = () => Date.now();

export const getKeyDown = keysDown =>
    supportedKeys.find(supportedKey => (!!keysDown[supportedKey] ? supportedKey : undefined));

export const getKeysDownAsArray = keysDown =>
    supportedKeys.filter(supportedKey => !!keysDown[supportedKey]);

const processSpawnRate = spawnRate => {
    let spawnRateBuffer = spawnRate;
    let amountOfTimesSpawnRateIncreasedCounter = 0;
    for (let i = 0; i <= spawnRateBuffer; i++) {
        if (Math.random() > collectibleSpawnChanceLowerLimit) {
            if (spawnRateBuffer < maximumCollectibleSpawnRate) {
                spawnRateBuffer = spawnRateBuffer + collectibleSpawnIncreasingRate;
            } else {
                spawnRateBuffer = maximumCollectibleSpawnRate;
            }
            amountOfTimesSpawnRateIncreasedCounter++;
        }
    }
    return {
        collectibleSpawnCounter: amountOfTimesSpawnRateIncreasedCounter,
        newSpawnRate: spawnRateBuffer,
    };
};

export const spawnCollectibles = (numberToSpawn, windowWidth) => {
    const newCollectibles = [];
    for (let i = 0; i < numberToSpawn; i++) {
        newCollectibles.push({
            positionX: Math.random() * (windowWidth - collectibleImageWidth),
            positionY: getCollectibleSpawnHeightInPixels(),
        });
    }
    return newCollectibles;
};

export const processCollectibleSpawning = (spawnRate, windowWidth) => {
    const {collectibleSpawnCounter, newSpawnRate} = processSpawnRate(spawnRate);
    const newCollectibles = spawnCollectibles(collectibleSpawnCounter, windowWidth);
    return {newSpawnRate, newCollectibles};
};

const playerAndCollectibleOverlap = (
    playerPositionX,
    windowHeight,
    collectiblePositionX,
    collectiblePositionY,
) => {
    const playerLeftBound = playerPositionX;
    const playerRightBound = playerPositionX + playerImageWidth;
    const collectibleLeftBound = collectiblePositionX;
    const collectibleRightBound = collectiblePositionX + collectibleImageWidth;
    const collectibleLowerBound = collectiblePositionY + collectibleImageHeight;
    const playerUpperBound = windowHeight - playerImageHeight;
    return (
        collectibleRightBound >= playerLeftBound &&
        collectibleLeftBound <= playerRightBound &&
        collectibleLowerBound >= playerUpperBound
    );
};

export const updateCollectible = (playerPosition, windowHeight) => collectible => {
    if (collectible.dead || collectible.caught) return collectible;

    const updatedCollectible = {};
    updatedCollectible.positionY = collectible.positionY + collectibleDropMovementIncrement;

    if (
        playerAndCollectibleOverlap(
            playerPosition,
            windowHeight,
            collectible.positionX,
            updatedCollectible.positionY,
        )
    ) {
        updatedCollectible.caught = true;
    } else if (updatedCollectible.positionY > getCollectibleDeathHeightInPixels(windowHeight)) {
        updatedCollectible.dead = true;
        updatedCollectible.positionY = getDeadCollectiblePositionY(windowHeight);
        updatedCollectible.positionX = collectible.positionX + collectibleDeadImageDifferenceX;
    }
    return {...collectible, ...updatedCollectible};
};

export const getScore = collectibles =>
    collectibles.filter(collectible => collectible.caught).length;

export const getGroundHeightInPixels = windowHeight => windowHeight;

export const getCollectibleSpawnHeightInPixels = () => -collectibleImageHeight;
export const getCollectibleDeathHeightInPixels = windowHeight =>
    getGroundHeightInPixels(windowHeight) - collectibleImageHeight / 2;

export const getDeadCollectiblePositionY = windowHeight =>
    getGroundHeightInPixels(windowHeight) - collectibleDeadImageDifference;

export const updateDeadPosition = (windowHeight, collectibles) =>
    collectibles.map(collectible => {
        if (!collectible.dead) {
            return collectible;
        } else {
            const updatedCollectible = {
                positionY: getDeadCollectiblePositionY(windowHeight),
            };
            return {...collectible, ...updatedCollectible};
        }
    });

export const getPlayerStartPosition = (windowWidth, playerImageWidth) =>
    windowWidth / 2 - playerImageWidth / 2;

export const splitCollectiblesToOptimiseUpdating = (
    collectibles,
    collectiblesToBeRenderedButNotUpdated,
) => {
    let newCollectiblesToBeRenderedButNotUpdated = [];
    const collectiblesStillRequiringUpdate = [];
    collectibles.forEach(collectible => {
        if (collectible.dead) {
            newCollectiblesToBeRenderedButNotUpdated.push(collectible);
        } else if (!collectible.caught) {
            collectiblesStillRequiringUpdate.push(collectible);
        }
    });

    collectiblesToBeRenderedButNotUpdated = [].concat(
        collectiblesToBeRenderedButNotUpdated,
        newCollectiblesToBeRenderedButNotUpdated,
    );

    if (collectiblesToBeRenderedButNotUpdated.length > deadCollectibleRenderLimit) {
        collectiblesToBeRenderedButNotUpdated = collectiblesToBeRenderedButNotUpdated.slice(
            collectiblesToBeRenderedButNotUpdated.length - deadCollectibleRenderLimit,
            collectiblesToBeRenderedButNotUpdated.length,
        );
    }

    return {collectiblesToBeRenderedButNotUpdated, collectiblesStillRequiringUpdate};
};

export const getCaughtCount = collectibles =>
    collectibles.filter(collectible => collectible.caught).length;

export const logPerformance = (
    logOnlyIfSlow,
    {updateTime, oldUpdateTime, spawnRate, collectibles, collectiblesToBeRenderedButNotUpdated},
) => {
    if (!logOnlyIfSlow || updateTime - oldUpdateTime > 60) {
        console.log(
            'Update difference: ',
            updateTime - oldUpdateTime,
            'spawn rate:',
            spawnRate,
            'collectibles',
            collectibles.length,
            'collectibles not being updated',
            collectiblesToBeRenderedButNotUpdated.length,
        );
    }
};
